import { useEffect, useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { Typography } from "@/core/components/ui/typography";
import type {
  IPayrollSheetApprovalAccessKey,
  SheetApprovalType,
} from "@/payroll/payroll.d";
import { SHEET_APPROVAL_TYPE } from "@/payroll/payroll.d";
import { getApprovalGate } from "./approval-types";
import { summarizeSheetNames } from "./utils";

export const APPROVAL_REASON_MAX_LENGTH = 2000;
// Counting up from zero on an optional field is noise; the count earns its place
// once the limit is actually in reach.
const COUNTER_VISIBLE_FROM = APPROVAL_REASON_MAX_LENGTH - 200;

interface ApprovalRemovalDialogProps {
  target: {
    accessKey: IPayrollSheetApprovalAccessKey;
    approvalType: SheetApprovalType;
  } | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason?: string) => void;
}

/**
 * Taking an approval back. Not styled as a destructive action: nothing is
 * deleted — the history keeps every action, and this appends the one that says
 * the sign-off no longer stands. The reason is optional and goes into that
 * record, which is the whole point of asking for it here.
 */
export const ApprovalRemovalDialog = ({
  target,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: ApprovalRemovalDialogProps) => {
  const [reason, setReason] = useState("");
  const open = Boolean(target);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const gate = getApprovalGate(
    target?.approvalType ?? SHEET_APPROVAL_TYPE.readyForTechApproval,
  );
  const accessKey = target?.accessKey;
  const trimmedReason = reason.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSubmitting) onOpenChange(nextOpen);
      }}
    >
      <DialogContent description={`Remove the ${gate.label} approval`}>
        <DialogHeader>
          <DialogTitle>Remove “{gate.label}”?</DialogTitle>
        </DialogHeader>
        <Stack gap="md" align="stretch" fullWidth className="px-6">
          <Stack
            gap="none"
            fullWidth
            className="rounded-lg border border-stroke-tertiary bg-surface-secondary px-4 py-3"
          >
            <Typography variant="subtitleW3">
              {accessKey?.name ?? "This sheet"}
            </Typography>
            {accessKey && (
              <Typography variant="captionW1" textColor="tertiary">
                {summarizeSheetNames(accessKey.sheet_names)}
              </Typography>
            )}
          </Stack>
          <Typography variant="bodyW2" textColor="secondary">
            These sheets go back to not approved. You can approve them again any
            time. Every change is saved, so you can always see who changed what.
          </Typography>
          <Stack gap="xs" align="stretch" fullWidth>
            <Textarea
              label="Reason (optional)"
              value={reason}
              maxLength={APPROVAL_REASON_MAX_LENGTH}
              disabled={isSubmitting}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Example: overtime hours for two techs still need checking"
            />
            {reason.length >= COUNTER_VISIBLE_FROM && (
              <Typography variant="captionW1" textColor="tertiary">
                {reason.length.toLocaleString()} of{" "}
                {APPROVAL_REASON_MAX_LENGTH.toLocaleString()} characters
              </Typography>
            )}
          </Stack>
        </Stack>
        <DialogFooter>
          <Button
            variant="tertiary"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Keep approval
          </Button>
          <Button
            loading={isSubmitting}
            onClick={() => onConfirm(trimmedReason || undefined)}
          >
            Remove approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
